import {
  ButtonDS,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  TextCapitalized,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
import {
  getSepaDirectDebitListAsync,
  selectGetSepaDirectDebitList,
  selectGetSepaDirectDebitListStatus,
} from "./sepaDirectDebitSlice";
import { SepaDirectDebitHistoryDisplayed } from "./sepaDirectDebitApi";
import { TriggerSepaDirectDebitModal } from "./modals/triggerSepaDirectDebitModal";
import { selectAdminUser } from "../homeSlice";

export const SepaDirectDebitHistoryTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const adminUser = useAppSelector(selectAdminUser);
  const sepaDirectDebitList = useAppSelector(selectGetSepaDirectDebitList);
  const sepaDirectDebitListStatus = useAppSelector(
    selectGetSepaDirectDebitListStatus,
  );

  const [listDisplayed, setListDisplayed] = useState<
    SepaDirectDebitHistoryDisplayed[] | null
  >(null);

  const [
    currentSepaDirectDebitUuid,
    setCurrentSepaDirectDebitUuid,
  ] = useState<string | null>(null);

  const authorizedAdmin = useMemo(() => {
    return [
      "p.chopin@qivia.fr",
      "m.gatinois@qivia.fr",
      "m.banjac@qivia.fr"
    ].includes(adminUser?.email ?? "");
  }, [adminUser]);

  const pageName = "sepaDirectDebitHistory";
  const apiRef = useGridApiRef();
  const key = "sepa-direct-debit-history-grid";

  const aggregation = {
    model: {
      company: "size",
      amount: "avg",
      sepaDirectDebitStatus: "size",
      transferStatus: "size",
      triggerType: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "sepaDirectDebitStatus",
        "transferStatus",
        "triggerType",
        "issuingTopUpStatus",
      ] as (keyof SepaDirectDebitHistoryDisplayed)[],
    [],
  );

  const findKeysToTranslate = useCallback(
    (
      key: keyof SepaDirectDebitHistoryDisplayed,
      value: string | number | null,
    ) => {
      if (!value) return value;
      switch (key) {
        case "sepaDirectDebitStatus":
          return t(`sepaDirectDebitHistory.sepaDirectDebitStatus.${value}`);
        case "transferStatus":
          return t(`sepaDirectDebitHistory.transferStatus.${value}`);
        case "triggerType":
          return t(`sepaDirectDebitHistory.triggerType.${value}`);
        case "issuingTopUpStatus":
          return t(`sepaDirectDebitHistory.issuingTopUpStatus.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const sepaDirectDebitListDisplayed: SepaDirectDebitHistoryDisplayed[] =
    useMemo(() => {
      const rowsWithId = sepaDirectDebitList.map((av) => {
        let newDict: SepaDirectDebitHistoryDisplayed & {
          id: string;
        } = {
          ...av,
          id: av.uuid,
        };
        Object.entries(av).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(
              key as keyof SepaDirectDebitHistoryDisplayed,
            )
          ) {
            newDict = {
              ...newDict,
              [key]: value
                ? findKeysToTranslate(
                    key as keyof SepaDirectDebitHistoryDisplayed,
                    value as string,
                  )
                : ("" as string),
            };
          }
        });
        return newDict;
      });
      return rowsWithId;
    }, [sepaDirectDebitList, keysToTranslate, findKeysToTranslate]);

  const searchBarProps = {
    values: sepaDirectDebitListDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  useEffect(() => {
    if (sepaDirectDebitListStatus === "idle") {
      void dispatch(getSepaDirectDebitListAsync());
    }
  }, [sepaDirectDebitListStatus, dispatch]);

  useEffect(() => {
    if (
      sepaDirectDebitListDisplayed &&
      sepaDirectDebitListStatus === "success"
    ) {
      setListDisplayed(sepaDirectDebitListDisplayed);
    }
  }, [sepaDirectDebitListStatus, sepaDirectDebitListDisplayed]);

  const columnsDisplayed: {
    [index in keyof SepaDirectDebitHistoryDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      uuid: {
        type: "string",
      },
      companyName: {
        type: "string",
      },
      amount: {
        type: "number",
      },
      mandate: {
        type: "string",
      },
      sepaDirectDebitStatus: {
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      sepaDirectDebitRequestedDate: {
        type: "string",
      },
      sepaDirectDebitReceivedDate: {
        type: "string",
      },
      sepaDirectDebitFailureReason: {
        type: "string",
      },
      transferStatus: {
        type: "string",
        valueFormatter: (value: string | null, row: GridValidRowModel) => {
          if (!value) return (
            <StyledColumnButton>
              <ButtonDS
                sizeButton="S"
                text={t("triggerSepaDirectDebit.button.title")}
                format={"hug"}
                buttonType={"primary"}
                onClick={() => {
                  if (!authorizedAdmin) {
                    return;
                  }
                  setCurrentSepaDirectDebitUuid(row["id"] as string);
                }}
                disabled={!authorizedAdmin}
              />
            </StyledColumnButton>
          );

          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      transferRequestedDate: {
        type: "string",
      },
      transferFailureReason: {
        type: "string",
      },
      issuingTopUpStatus: {
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      issuingTopUpSucceededAt: {
        type: "string",
      },
      triggerType: {
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
    };
  }, [authorizedAdmin, t]);

  const currentSepaDirectDebit = useMemo(() => {
    return listDisplayed?.find(
      (t) => t.uuid === currentSepaDirectDebitUuid,
    );
  }, [listDisplayed, currentSepaDirectDebitUuid]);

  if (params.tab !== "sepaDirectDebitHistory") {
    return;
  }

  if (sepaDirectDebitListStatus === "processing" || !listDisplayed) {
    return <LaunchPage hasBorderRadius={true} />;
  }

  return (
    <StyledContainer>
      <Spacer y={1} />
      <MaterialUiTable<
        keyof SepaDirectDebitHistoryDisplayed,
        SepaDirectDebitHistoryDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
      {currentSepaDirectDebit &&
        <TriggerSepaDirectDebitModal
          visible={!!currentSepaDirectDebitUuid}
          onCloseModal={() => {
            setCurrentSepaDirectDebitUuid(null);
          }}
          sepaDirectDebit={{
            uuid: currentSepaDirectDebit.uuid,
            companyName: currentSepaDirectDebit.companyName,
            amount: currentSepaDirectDebit.amount,
          }}
        />
        }
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledColumnButton = styled.div`
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;
height: 100%;
`;
